import { useTranslation } from "react-i18next";
// WhoChallengedYou — dashboard viral loop card (Steptember P1).
//
// Shows the last 3 unique users who challenged the current user, with a
// 1-tap "Daag terug uit" CTA that opens the challenge creation wizard
// pre-filled with that opponent. Nothing renders until the API returns
// at least one challenger, so the card never occupies dashboard real
// estate for empty accounts.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, ArrowRight } from "lucide-react";
import api from "@/api";
import { resolveBackendUrl } from "@/lib/resolveBackendUrl";
const RELATIVE_LABEL = iso => {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const day = 24 * 3600 * 1000;
    if (diff < 60_000) return "net";
    if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
    if (diff < day) return `${Math.floor(diff / 3600_000)}u`;
    if (diff < 7 * day) return `${Math.floor(diff / day)}d`;
    return `${Math.floor(diff / (7 * day))}w`;
  } catch {
    return "";
  }
};
export default function WhoChallengedYou() {
  const {
    t
  } = useTranslation();
  const nav = useNavigate();
  const [items, setItems] = useState(null); // null=loading, []=empty, [...]=rows

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const {
          data
        } = await api.get("/challenges/who-challenged-me");
        if (!cancel) setItems(data?.items || []);
      } catch {
        if (!cancel) setItems([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  // Loading OR empty — render nothing so the block is invisible until
  // there's a real viral moment to surface.
  if (items === null || items.length === 0) return null;
  const challengeBack = userId => {
    nav(`/challenges?opponents=${encodeURIComponent(userId)}`);
  };
  return <div className="px-4 pt-3" data-testid="who-challenged-you">
            <div className="tile">
                <div className="flex items-center gap-2 mb-3">
                    <div className="h-7 w-7 rounded-full bg-[#FF453A]/15 border border-[#FF453A]/30 flex items-center justify-center shrink-0">
                        <Swords size={13} className="text-[#FF453A]" />
                    </div>
                    <p className="text-[10.5px] uppercase tracking-wider font-bold text-[#FF453A] flex-1">
                        {t("whoChallengedYou.wie_daagde_je_uit")}
                    </p>
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                        {items.length}
                    </span>
                </div>

                <ul className="space-y-2" data-testid="who-challenged-you-list">
                    {items.map(it => {
          return <li key={it.user_id}>
                            <button type="button" onClick={() => challengeBack(it.user_id)} data-testid={`challenge-back-${it.user_id}`} className="w-full rounded-2xl bg-secondary/50 hover:bg-secondary
                                           border border-border/50 px-3 py-2.5
                                           flex items-center gap-3 text-left
                                           active:scale-[0.99] transition">
                                {/* Avatar */}
                                {it.avatar_url ? <img src={resolveBackendUrl(it.avatar_url)} alt={it.name} className="h-10 w-10 rounded-full object-cover ring-1 ring-border shrink-0" onError={e => {
                e.currentTarget.style.display = "none";
              }} /> : <div className="h-10 w-10 rounded-full bg-foreground text-background flex items-center justify-center font-display text-[13px] font-semibold shrink-0">
                                        {(it.name || "?").charAt(0).toUpperCase()}
                                    </div>}

                                <div className="flex-1 min-w-0">
                                    <p className="text-[13.5px] font-semibold truncate">
                                        {it.name}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground truncate">
                                        {it.challenge_title || "daagde je uit"}
                                        {it.challenged_at && <> · <span className="tabular-nums">{RELATIVE_LABEL(it.challenged_at)}</span></>}
                                    </p>
                                </div>

                                <span className="rounded-full bg-[#FF453A]/15 text-[#FF453A]
                                                 border border-[#FF453A]/30
                                                 px-2.5 py-1 text-[10.5px] font-bold
                                                 flex items-center gap-1 shrink-0">
                                    {t("whoChallengedYou.terug")} <ArrowRight size={10} />
                                </span>
                            </button>
                        </li>;
        })}
                </ul>
            </div>
        </div>;
}